import { useQuery, useMutation } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

// ============= TYPES =============

export interface ListBuilderContact {
  id: string;
  company_id: string | null;
  full_name: string;
  first_name: string | null;
  last_name: string | null;
  job_title: string | null;
  seniority_level: string | null;
  department: string | null;
  buyer_segment: string | null;
  is_decision_maker: boolean | null;
  email: string | null;
  email_status: string | null;
  email_verified: boolean | null;
  linkedin_url: string | null;
  outreach_status: string | null;
  location: string | null;
  source: string | null;
  company_name: string | null;
  company_domain: string | null;
  company_country: string | null;
  company_category: string | null;
  company_icp_tier: string | null;
  company_top_account: boolean | null;
  company_modalities: string[] | null;
  company_therapeutic_areas: string[] | null;
  company_carr_modality: string | null;
  company_development_stage: string | null;
  company_clinical_phase: string | null;
  company_employee_range: string | null;
  company_funding_status: string | null;
  company_region: string | null;
  company_ai_subcategory: string | null;
  company_sales_rep: string | null;
  company_match_type: string | null;
  company_domain_status: string | null;
  company_carr_highest_phase: string | null;
  company_territory: string | null;
  campaign_count: number;
  active_campaign_count: number;
  last_campaign_date: string | null;
}

export interface ListBuilderFilters {
  searchTerm?: string;
  seniorityLevels?: string[];
  departments?: string[];
  buyerPersonas?: string[];
  marketingPersonas?: string[];
  hasEmail?: boolean;
  hasLinkedIn?: boolean;
  emailVerifiedOnly?: boolean;
  emailStatuses?: string[];
  decisionMakersOnly?: boolean;
  outreachStatuses?: string[];
  companyCategories?: string[];
  companyCountries?: string[];
  topAccountsOnly?: boolean;
  carrModalities?: string[];
  modalities?: string[];
  employeeRanges?: string[];
  fundingStatuses?: string[];
  accountTiers?: string[];
  icpTiers?: string[];
  clinicalPhases?: string[];
  therapeuticAreas?: string[];
  subcategories?: string[];
  territories?: string[];
  campaignStatuses?: string[];
  lastCampaignAfter?: string;
  lastCampaignBefore?: string;
  limit?: number;
  offset?: number;
}

export interface ListBuilderFilterOptions {
  seniorityLevels: string[];
  departments: string[];
  buyerPersonas: string[];
  marketingPersonas: string[];
  emailStatuses: string[];
  outreachStatuses: string[];
  companyCategories: string[];
  companyCountries: string[];
  carrModalities: string[];
  modalities: string[];
  employeeRanges: string[];
  fundingStatuses: string[];
  accountTiers: string[];
  icpTiers: string[];
  clinicalPhases: string[];
  therapeuticAreas: string[];
  subcategories: string[];
  territories: string[];
}

// ============= FILTER APPLICATION HELPER =============

function applyFilters(query: any, filters: ListBuilderFilters) {
  if (filters.searchTerm?.trim()) {
    const term = filters.searchTerm.trim();
    query = query.or(
      `full_name.ilike.%${term}%,job_title.ilike.%${term}%,company_name.ilike.%${term}%,email.ilike.%${term}%`
    );
  }
  if (filters.seniorityLevels?.length) {
    query = query.in('seniority_level', filters.seniorityLevels);
  }
  if (filters.departments?.length) {
    query = query.in('department', filters.departments);
  }
  if (filters.buyerPersonas?.length) {
    query = query.in('buyer_segment', filters.buyerPersonas);
  }
  if (filters.hasEmail === true) {
    query = query.not('email', 'is', null);
  }
  if (filters.hasLinkedIn === true) {
    query = query.not('linkedin_url', 'is', null);
  }
  if (filters.emailVerifiedOnly === true) {
    query = query.eq('email_verified', true);
  }
  if (filters.emailStatuses?.length) {
    query = query.in('email_status', filters.emailStatuses);
  }
  if (filters.decisionMakersOnly === true) {
    query = query.eq('is_decision_maker', true);
  }
  if (filters.outreachStatuses?.length) {
    query = query.in('outreach_status', filters.outreachStatuses);
  }
  if (filters.companyCategories?.length) {
    query = query.in('company_category', filters.companyCategories);
  }
  if (filters.companyCountries?.length) {
    query = query.in('company_country', filters.companyCountries);
  }
  if (filters.topAccountsOnly) {
    query = query.eq('company_top_account', true);
  }
  if (filters.carrModalities?.length) {
    query = query.in('company_carr_modality', filters.carrModalities);
  }
  if (filters.modalities?.length) {
    query = query.overlaps('company_modalities', filters.modalities);
  }
  if (filters.employeeRanges?.length) {
    query = query.in('company_employee_range', filters.employeeRanges);
  }
  if (filters.fundingStatuses?.length) {
    const hasPublic = filters.fundingStatuses.includes('Public Company');
    const others = filters.fundingStatuses.filter((f) => f !== 'Public Company');
    if (hasPublic && others.length) {
      query = query.or(
        `company_funding_status.like.Public%,company_funding_status.in.(${others.join(',')})`
      );
    } else if (hasPublic) {
      query = query.like('company_funding_status', 'Public%');
    } else {
      query = query.in('company_funding_status', others);
    }
  }
  if (filters.icpTiers?.length) {
    query = query.in('company_icp_tier', filters.icpTiers);
  }
  if (filters.therapeuticAreas?.length) {
    query = query.overlaps('company_therapeutic_areas', filters.therapeuticAreas);
  }
  if (filters.marketingPersonas?.length) {
    query = query.in('marketing_persona', filters.marketingPersonas);
  }
  if (filters.accountTiers?.length) {
    query = query.in('company_account_tier', filters.accountTiers);
  }
  if (filters.clinicalPhases?.length) {
    query = query.in('company_clinical_phase', filters.clinicalPhases);
  }
  if (filters.subcategories?.length) {
    query = query.in('company_ai_subcategory', filters.subcategories);
  }
  if (filters.territories?.length) {
    query = query.in('company_territory', filters.territories);
  }
  if (filters.campaignStatuses?.length) {
    const hasNever = filters.campaignStatuses.includes('never');
    const hasActive = filters.campaignStatuses.includes('in_active');
    const hasArchived = filters.campaignStatuses.includes('in_archived');
    if (hasNever && !hasActive && !hasArchived) {
      query = query.eq('campaign_count', 0);
    } else if (!hasNever && hasActive) {
      query = query.gt('active_campaign_count', 0);
    } else if (!hasNever && hasArchived && !hasActive) {
      query = query.gt('campaign_count', 0).eq('active_campaign_count', 0);
    } else if (!hasNever) {
      query = query.gt('campaign_count', 0);
    }
    // If all three selected, no filter needed (shows everything)
  }
  if (filters.lastCampaignAfter) {
    query = query.gte('last_campaign_date', filters.lastCampaignAfter);
  }
  if (filters.lastCampaignBefore) {
    query = query.lte('last_campaign_date', filters.lastCampaignBefore);
  }
  // Always hide inactive (disqualified) accounts
  query = query.neq('company_account_tier', 'disqualified');
  return query;
}

// ============= HOOKS =============

export function useListBuilderContacts(filters: ListBuilderFilters) {
  return useQuery({
    queryKey: ['list-builder-contacts', filters],
    queryFn: async () => {
      let query = supabase
        .from('v_contacts_with_company' as any)
        .select('*', { count: 'exact' });

      query = applyFilters(query, filters);

      query = query
        .order('full_name', { ascending: true, nullsFirst: false })
        .range(filters.offset || 0, (filters.offset || 0) + (filters.limit || 50) - 1);

      const { data, error, count } = await query;
      if (error) throw error;

      return {
        data: (data || []) as unknown as ListBuilderContact[],
        totalCount: count || 0,
      };
    },
    staleTime: 60 * 1000,
  });
}

export function useListBuilderFilterOptions() {
  return useQuery({
    queryKey: ['list-builder-filter-options'],
    queryFn: async (): Promise<ListBuilderFilterOptions> => {
      const view = 'v_contacts_with_company' as any;
      const companiesTable = 'companies' as any;

      // Use shared account filter views for company filters (consistent with Accounts page)
      // Contact-level filters still come from the contacts view
      const [seniority, departments, personas, marketingPersonas, emailStatuses, outreachStatuses,
             categories, countries, empRanges,
             carrModalities, modalities, funding, accountTiers, icpTiers, clinicalPhases,
             therapeuticAreas, aiSubcategories, territories] =
        await Promise.all([
          supabase.from(view).select('seniority_level').not('seniority_level', 'is', null).limit(500),
          supabase.from(view).select('department').not('department', 'is', null).limit(500),
          supabase.from(view).select('buyer_segment').not('buyer_segment', 'is', null).limit(500),
          supabase.from(view).select('marketing_persona').not('marketing_persona', 'is', null).limit(500),
          supabase.from(view).select('email_status').not('email_status', 'is', null).limit(500),
          supabase.from(view).select('outreach_status').not('outreach_status', 'is', null).limit(500),
          // Company filters from shared views
          supabase.from('v_filter_primary_categories').select('primary_category'),
          supabase.from('v_filter_countries').select('headquarters_country'),
          supabase.from('v_filter_employee_ranges').select('employee_range'),
          // Modality Type (Allogeneic/Autologous/Both) from carr_modality
          (supabase.from('v_filter_carr_modalities') as any).select('carr_modality, sort_order').order('sort_order'),
          // Canonical cell type modalities (CAR-T, NK, etc.)
          (supabase.from('v_filter_modalities') as any).select('modality, sort_order').order('sort_order'),
          supabase.from(companiesTable).select('funding_status').eq('is_icp_relevant', true).not('funding_status', 'is', null).limit(500),
          supabase.from(companiesTable).select('account_tier').eq('is_icp_relevant', true).not('account_tier', 'is', null).limit(500),
          supabase.from(companiesTable).select('icp_tier').eq('is_icp_relevant', true).not('icp_tier', 'is', null).limit(500),
          supabase.from(companiesTable).select('clinical_phase').eq('is_icp_relevant', true).not('clinical_phase', 'is', null).limit(500),
          (supabase.from('v_filter_therapeutic_areas') as any).select('therapeutic_area, sort_order').order('sort_order'),
          (supabase.from('v_filter_ai_subcategories') as any).select('val'),
          supabase.from('v_filter_territories').select('territory'),
        ]);

      const unique = (arr: any[], key: string) =>
        [...new Set((arr || []).map((d: any) => d[key]).filter(Boolean))].sort() as string[];

      // Simplify funding_status: group "Public (NASDAQ: ...)" variants into "Public Company"
      const rawFunding = unique(funding.data, 'funding_status');
      const simplifiedFunding = [...new Set(rawFunding.map((f) =>
        f.startsWith('Public (') ? 'Public Company' : f
      ))].sort();

      // Preserve sort order from views for carr modalities and modalities
      const orderedList = (arr: any[], key: string) =>
        (arr || []).map((d: any) => d[key]).filter(Boolean) as string[];

      return {
        seniorityLevels: unique(seniority.data, 'seniority_level'),
        departments: unique(departments.data, 'department'),
        buyerPersonas: unique(personas.data, 'buyer_segment'),
        marketingPersonas: unique(marketingPersonas.data, 'marketing_persona'),
        emailStatuses: unique(emailStatuses.data, 'email_status'),
        outreachStatuses: unique(outreachStatuses.data, 'outreach_status'),
        companyCategories: unique(categories.data, 'primary_category'),
        companyCountries: unique(countries.data, 'headquarters_country'),
        carrModalities: orderedList(carrModalities.data, 'carr_modality'),
        modalities: orderedList(modalities.data, 'modality'),
        employeeRanges: unique(empRanges.data, 'employee_range'),
        fundingStatuses: simplifiedFunding,
        accountTiers: unique(accountTiers.data, 'account_tier'),
        icpTiers: unique(icpTiers.data, 'icp_tier'),
        clinicalPhases: unique(clinicalPhases.data, 'clinical_phase'),
        therapeuticAreas: orderedList(therapeuticAreas.data, 'therapeutic_area'),
        subcategories: unique(aiSubcategories.data, 'val'),
        territories: unique(territories.data, 'territory'),
      };
    },
    staleTime: 30 * 60 * 1000,
  });
}

export function useExportListBuilder() {
  return useMutation({
    mutationFn: async (filters: ListBuilderFilters): Promise<ListBuilderContact[]> => {
      let query = supabase
        .from('v_contacts_with_company' as any)
        .select('*');

      query = applyFilters(query, filters);
      query = query.order('full_name', { ascending: true, nullsFirst: false }).limit(10000);

      const { data, error } = await query;
      if (error) throw error;
      return (data || []) as unknown as ListBuilderContact[];
    },
  });
}

export function useExistingCampaigns() {
  return useQuery({
    queryKey: ['list-builder-campaigns'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('campaigns')
        .select('id, name, platform, status, territory')
        .neq('status', 'archived')
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data || [];
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useCreateCampaignWithContacts() {
  return useMutation({
    mutationFn: async ({
      campaignName,
      platform,
      territory,
      contacts,
      sendingPersonaId,
      createdByUserId,
    }: {
      campaignName: string;
      platform: string;
      territory?: string;
      contacts: ListBuilderContact[];
      sendingPersonaId?: string | null;
      createdByUserId?: string | null;
    }) => {
      const { data: campaign, error: campaignError } = await supabase
        .from('campaigns')
        .insert({
          name: campaignName.trim(),
          platform,
          territory: territory || null,
          status: 'planning',
          created_at: new Date().toISOString(),
          ...(sendingPersonaId ? { sending_persona_id: sendingPersonaId } : {}),
          ...(createdByUserId ? { created_by_user_id: createdByUserId } : {}),
        })
        .select('id')
        .single();

      if (campaignError) throw campaignError;

      const rows = contacts.map((c) => ({
        campaign_id: campaign.id,
        contact_id: c.id,
        engagement_status: 'pending',
        enrolled_at: new Date().toISOString(),
      }));

      const { error: contactsError } = await supabase
        .from('campaign_contacts')
        .insert(rows);

      if (contactsError) throw contactsError;
      return campaign;
    },
  });
}

export function useAddToCampaign() {
  return useMutation({
    mutationFn: async ({
      campaignId,
      contacts,
    }: {
      campaignId: string;
      contacts: ListBuilderContact[];
    }) => {
      const rows = contacts.map((c) => ({
        campaign_id: campaignId,
        contact_id: c.id,
        engagement_status: 'pending',
        enrolled_at: new Date().toISOString(),
      }));

      const { error } = await supabase
        .from('campaign_contacts')
        .insert(rows);

      if (error) throw error;
      return { count: rows.length };
    },
  });
}
